import { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, EyeOff, Eye, RotateCcw, Check } from "lucide-react";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger } from "@/components/ui/context-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const storeKey = (k) => `cols:${localStorage.getItem("uid") || "u"}:${k}`;
const loadPrefs = (k) => { try { return JSON.parse(localStorage.getItem(storeKey(k))) || {}; } catch { return {}; } };

export const qtyPieces = (p) => (p != null && p !== 0 ? Math.abs(p) : null);

// columns: [{ key, label, render(row), sort(row) | false, align: "right", className, headClassName, fixed }]
export const SmartTable = ({ tableKey, columns, rows, rowKey = (r) => r.id, rowProps, rowWrap, empty = "Aucune donnée.", testid }) => {
  const [prefs, setPrefs] = useState(() => loadPrefs(tableKey));
  const save = (p) => { setPrefs(p); localStorage.setItem(storeKey(tableKey), JSON.stringify(p)); };
  const order = useMemo(() => {
    const base = columns.map((c) => c.key);
    const saved = (prefs.order || []).filter((k) => base.includes(k));
    return [...saved, ...base.filter((k) => !saved.includes(k))];
  }, [columns, prefs.order]);
  const hidden = prefs.hidden || [];
  const visible = order.map((k) => columns.find((c) => c.key === k)).filter((c) => c && !hidden.includes(c.key));
  const sort = prefs.sort;
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || col.sort === false) return rows;
    const get = col.sort || ((r) => { const v = col.render(r); return typeof v === "object" ? "" : v; });
    return [...rows].sort((a, b) => {
      const va = get(a), vb = get(b);
      const r = typeof va === "number" && typeof vb === "number" ? va - vb : String(va ?? "").localeCompare(String(vb ?? ""), "fr", { numeric: true });
      return sort.dir === "desc" ? -r : r;
    });
  }, [rows, sort, columns]);
  const move = (key, delta) => { const o = [...order]; const i = o.indexOf(key); const j = i + delta; if (j < 0 || j >= o.length) return; [o[i], o[j]] = [o[j], o[i]]; save({ ...prefs, order: o }); };
  const toggle = (key) => save({ ...prefs, hidden: hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key] });
  const setSort = (key, dir) => save({ ...prefs, sort: dir ? { key, dir } : null });
  const reset = () => { localStorage.removeItem(storeKey(tableKey)); setPrefs({}); };
  const align = (c) => (c.align === "right" ? "text-right" : "");
  const headMenu = (c) => (
    <ContextMenuContent className="w-64" data-testid="col-context-menu">
      <ContextMenuLabel className="text-xs">Colonne « {c.label} »</ContextMenuLabel><ContextMenuSeparator />
      {c.sort !== false && <>
        <ContextMenuItem onSelect={() => setSort(c.key, "asc")} data-testid="col-sort-asc"><ArrowUp size={14} className="mr-2" /> Trier croissant {sort?.key === c.key && sort.dir === "asc" && <Check size={12} className="ml-auto" />}</ContextMenuItem>
        <ContextMenuItem onSelect={() => setSort(c.key, "desc")} data-testid="col-sort-desc"><ArrowDown size={14} className="mr-2" /> Trier décroissant {sort?.key === c.key && sort.dir === "desc" && <Check size={12} className="ml-auto" />}</ContextMenuItem>
        {sort && <ContextMenuItem onSelect={() => setSort(null, null)}>Annuler le tri</ContextMenuItem>}
        <ContextMenuSeparator /></>}
      <ContextMenuItem onSelect={() => move(c.key, -1)} data-testid="col-move-left"><ArrowLeft size={14} className="mr-2" /> Déplacer à gauche</ContextMenuItem>
      <ContextMenuItem onSelect={() => move(c.key, 1)} data-testid="col-move-right"><ArrowRight size={14} className="mr-2" /> Déplacer à droite</ContextMenuItem>
      <ContextMenuItem onSelect={() => toggle(c.key)} disabled={c.fixed} data-testid="col-hide"><EyeOff size={14} className="mr-2" /> Masquer cette colonne</ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuSub>
        <ContextMenuSubTrigger><Eye size={14} className="mr-2" /> Colonnes affichées</ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-56">{columns.map((col) => <ContextMenuItem key={col.key} disabled={col.fixed} onSelect={(e) => { e.preventDefault(); toggle(col.key); }} data-testid={`col-toggle-${col.key}`}><span className="mr-2 inline-block w-3">{!hidden.includes(col.key) && <Check size={12} />}</span>{col.label}</ContextMenuItem>)}</ContextMenuSubContent>
      </ContextMenuSub>
      <ContextMenuItem onSelect={reset} data-testid="col-reset"><RotateCcw size={14} className="mr-2" /> Réinitialiser le tableau</ContextMenuItem>
    </ContextMenuContent>
  );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {visible.map((c) => (
            <ContextMenu key={c.key}>
              <ContextMenuTrigger asChild>
                <TableHead className={`cursor-context-menu select-none ${align(c)} ${c.headClassName || ""}`} title="Clic droit : trier, masquer, déplacer" onClick={() => c.sort !== false && setSort(c.key, sort?.key === c.key && sort.dir === "asc" ? "desc" : "asc")} data-testid={`col-head-${c.key}`}>
                  <span className="inline-flex items-center gap-1">{c.label}{sort?.key === c.key && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}</span>
                </TableHead>
              </ContextMenuTrigger>
              {headMenu(c)}
            </ContextMenu>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody data-testid={testid}>
        {sorted.length === 0 && <TableRow><TableCell colSpan={visible.length || 1} className="text-center text-muted-foreground">{empty}</TableCell></TableRow>}
        {sorted.map((r) => {
          const row = <TableRow {...(rowProps?.(r) || {})}>{visible.map((c) => <TableCell key={c.key} className={`${align(c)} ${typeof c.className === "function" ? c.className(r) : c.className || ""}`}>{c.render(r)}</TableCell>)}</TableRow>;
          return rowWrap ? rowWrap(r, row) : <TableRow key={rowKey(r)} {...(rowProps?.(r) || {})}>{visible.map((c) => <TableCell key={c.key} className={`${align(c)} ${typeof c.className === "function" ? c.className(r) : c.className || ""}`}>{c.render(r)}</TableCell>)}</TableRow>;
        })}
      </TableBody>
    </Table>
  );
};
